import { useMediaQuery } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LoadingContext, M_StateActions, MTabsInpage, NavigateContext } from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import NonSpotTenderConfig from './NonSpotTenderConfig';
import TenderPhaseConfig from './TenderPhaseConfig';
import TenderInQuotation from '../Tenders/TenderInQuotation';
import TenderFinished from '../Tenders/TenderFinished';
import { hasPermission } from 'src/components/utils/permissionsHelper';

// unified tabs styling
import 'src/pages/tabsInpage.css';

export default function NonSpotTenderPage({ roles, onCreateTab, toastMessages, handleTabClose }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const matches = useMediaQuery('(min-width:900px)');
    const M_State = useSelector((state) => state.StateKeyValue);
    const location = useLocation();
    const { renameTab } = useContext(NavigateContext);
    const selectedTab = useSelector(s => s.StateKeyValue.find(o => o.key === 'selectedTab')?.value);

    const [id, setId] = useState(() => M_State.find(obj => obj.key === 'selectedTab')?.value);

    // Initialize menu and refresh state from session storage
    const [menu, setMenu] = useState(1);
    const [refresh, setRefresh] = useState(false);

    //============================================================================================================
    // EFFECT
    // Rename tab when selectedTab changes
    //============================================================================================================
    useEffect(() => {
        if (!selectedTab) return; // If no selected tab, do nothing
        renameTab(String(selectedTab), t('NonSpotTenders'));
    }, [t, selectedTab]);

    //============================================================================================================
    // Funtio nto upsert session data
    //============================================================================================================
    const upsertSession = (tabId, kv) => {
        const current = M_State.find(o => o.key === tabId)?.value || []; // Get current session data
        const filtered = current.filter(e => e.key !== kv.key); // filter out existing key
        dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] })); // Dispatch updated session data
    };

    //============================================================================================================
    // Function to get session storage data based on session ID
    //============================================================================================================
    const getSessionStorageData = (sessionID) => {
        ctx.setIsLoad(true);
        const session = M_State.find(obj => obj.key === sessionID)?.value; // Get the session data for this page
        if (session) { // If session data exists, retrieve the menu and refresh values
            const m = session.find(x => x.key === 'menu')?.value; // Find the saved menu value
            const ref = session.find(x => x.key === 'refresh')?.value; // Find the saved refresh value
            if (typeof m === 'number') setMenu(m); else setMenu(1); // Default to 1 if not found
            if (ref !== undefined) setRefresh(ref); // Set refresh state if it exists
        } else {
            setMenu(1); // Default to 1 if no session data
        }
        ctx.setIsLoad(false);
    };

    //============================================================================================================
    // EFFECT
    // Effect to get session storage data when the component mounts or pathname changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return; // If no ID, do nothing
        getSessionStorageData(id);
        //setRefresh(r => !r); // Toggle refresh state to trigger re-render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    //============================================================================================================
    // EFFECT
    // Effect to handle tab changes
    //============================================================================================================
    useEffect(() => {
        const newId = M_State.find(obj => obj.key === 'selectedTab')?.value; // Get the new selected tab ID
        if (newId && newId !== id) { // If the new ID is different from the current ID
            setId(newId); // Update the current ID
            getSessionStorageData(newId); // Restore session data for the new tab
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [M_State]);

    //============================================================================================================
    // EFFECT
    // Effect to update session storage when menu or refresh state changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        // Persist menu state
        upsertSession(id, { key: 'menu', value: menu });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, menu]);

    //============================================================================================================
    // EFFECT
    // Effect to update session storage when refresh state changes
    //============================================================================================================
    useEffect(() => {
        if (!id) return;
        upsertSession(id, { key: 'refresh', value: refresh });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, refresh]);

    //============================================================================================================
    // Function to determine which tabs to show based on permissions
    //============================================================================================================
    const allowedTabs = () => {
        const tabArray = [
            { id: 1, labelTab: t('NonSpotTendersConfiguration'), iconTab: <i className="pi pi-wrench"></i> },
            { id: 2, labelTab: t('InfoTenderPhases'), iconTab: <i className="pi pi-info-circle"></i> }
        ];

        // If has permissions to update on Tenders module, render tabs id 3 and 4
        if (hasPermission(roles, 'Tenders', 'Update') || hasPermission(roles, 'Tenders', 'Admin')) {
            tabArray.push(
                { id: 3, labelTab: t('NonSpotTendersInQuotation'), iconTab: <i className="pi pi-chart-line"></i> },
                { id: 4, labelTab: t('NonSpotTendersFinished'), iconTab: <i className="pi pi-check-square"></i> }
            );
        }

        return tabArray;
    };

    return (
        // Backward-compat bridge + unified layout classes (visual only)
        <div className="tabsInpage divGlobalSettings">
            <div className="tabsInpage__title titleSettings" style={{ color: ctx.secondaryColor }}>
                {t('NonSpotTenders')}
            </div>
            {/* Standard body split: sidebar (tabs) + content (panel) */}
            <div className="tabsInpage__body">
                {/* Sidebar column (controlled by CSS: widths, separator, responsive) */}
                <div className="tabsInpage__sidebar">
                    <MTabsInpage // Tabs for Non-Spot Tenders Page
                        type={matches ? 'column' : 'tabs'}
                        renderTabs={allowedTabs()}
                        firstTabOpen={menu}
                        onGetTab={(obj) => setMenu(obj.id)}
                        fixedArea={false}
                    />
                </div>
                {/* Right content panel. Legacy class kept for bridge; no behavior changes */}
                <div className="tabsInpage__content settingsPageMainDiv">
                    {menu === 1 && (
                        <NonSpotTenderConfig // Configuration component for Non-Spot Tenders with Non Spot Tender dynamic table
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 2 && (
                        <TenderPhaseConfig // Configuration component for Tender Phases with tender phases dynamic table
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 3 && (
                        <TenderInQuotation // Component for Non-Spot Tenders in quotation phase
                            tenderTypeId={2}               // NonSPOT signal via Props
                            mode="nonspot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                    {menu === 4 && (
                        <TenderFinished // Component for finished Non-Spot Tenders
                            tenderTypeId={2}               // NonSPOT signal via Props
                            mode="nonspot"
                            roles={roles}
                            onCreateTab={onCreateTab}
                            toastMessages={toastMessages}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
